/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Input, Row, Select, Typography} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import PhoneNumberInput from '../../../../../components/CustomInputs/PhoneNumberInput/PhoneNumberInput';
import MomentDatePicker from '../../../../../components/DatePicker/MomentDatePicker';
import {filterSelectOptions} from '../../../../../components/Forms/Utils/commonFunctions';
import {CommonErrors, transformInput} from '../../../../../constants/Common';
import FilteredUnit from '../../../../../constants/FilteredUnit';
import InformativeText from '../../../../../constants/InformativeText';
import country from '../../../../../services/modules/JSON/country';
import NumberInput from '../../../../../shared/NumberInput';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import useUserStore from '../../../../../store/userStore/userStore';
import {
  FirstCap,
  eachWordCapOrAllCap
} from '../../../../../utils/FormUtils/FirstCap';
import RegEx from '../../../../../utils/FormUtils/RegEx';
import {rowGutter} from '../../Constants/Common';
import CountryWiseCreds from '../../utils/CountryWiseCreds';
import {financialYearDropdown} from '../constants/commonConstants';
import {OPKey, OPLabel} from '../constants/orgProfileKeyLabel';

const GeneralDisclosureForm: React.FC = () => {
  const form = useFormInstance();
  const {getFieldValue, setFields} = form;
  const {category, subCategory, typeOfBusiness} = useJSONStore.getState();
  const countryOptions = useJSONStore((state) => state.country);
  const [countrySelected, setCountrySelected] = useState<number>(
    getFieldValue(OPKey.COMPANY_COUNTRY)
  );
  const {preUnits} = useCompanyStore.getState();
  const [preferredUnits, setPreferredUnits] = useState(preUnits);
  const GDInfo = InformativeText?.OM;

  useEffect(() => {
    Promise.all([country()])
      .catch(() => {})
      .finally(() => {});
  }, []);

  const userEmailId = useUserStore.getState().userDetails.email;
  const normalizeEmailForComparison = (email?: string) =>
    email?.trim().toLowerCase();

  const colSpan = 24;

  return (
    <Row gutter={rowGutter} style={{padding: '10px 30px'}}>
      <Typography.Title level={4} style={{margin: '10px 0 20px 10px'}}>
        General Details
      </Typography.Title>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.COMPANY_NAME}
          label={OPLabel.COMPANY_NAME}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.COMPANY_NAME)
            },
            {
              max: 100,
              message: `${OPLabel.COMPANY_NAME} Should not be more than 100 characters`
            },
            {
              min: 3,
              message: `${OPLabel.COMPANY_NAME} Should be more than 2 characters`
            },
            {
              pattern: RegEx.businessName,
              message: CommonErrors.noAllowedSpecChar
            },
            {
              whitespace: true,
              message: CommonErrors.mandatory(OPLabel.COMPANY_NAME)
            }
          ]}
          validateFirst={true}
          hasFeedback
          normalize={(value) => transformInput.removeExtraSpace(value)}
        >
          <Input
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = eachWordCapOrAllCap(target.value);
            }}
            placeholder={`Enter ${OPLabel.COMPANY_NAME}`}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.CATEGORY}
          label={OPLabel.CATEGORY}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.CATEGORY)
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Select
            placeholder={`Choose ${OPLabel.CATEGORY}`}
            options={category}
            disabled
            onChange={() => {
              setFields([
                {
                  name: OPKey.SUB_CATEGORY,
                  errors: ['Please Select Sub-Category'],
                  value: undefined
                }
              ]);
            }}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item dependencies={[OPKey.CATEGORY]}>
          {({getFieldValue}) => {
            return (
              <Form.Item
                name={OPKey.SUB_CATEGORY}
                label={OPLabel.SUB_CATEGORY}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(OPLabel.SUB_CATEGORY)
                  }
                ]}
                shouldUpdate
                validateFirst={true}
                hasFeedback
                style={{marginBottom: '0px'}}
              >
                <Select
                  placeholder={`Choose ${OPLabel.SUB_CATEGORY}`}
                  // disabled={!getFieldValue(OPKey.CATEGORY)}
                  disabled
                  options={
                    getFieldValue(OPKey.CATEGORY)
                      ? subCategory
                          .filter(
                            (item) =>
                              item?.categoryId === getFieldValue(OPKey.CATEGORY)
                          )
                          .map((item) => {
                            return {value: item.value, label: item.label};
                          })
                      : [{value: 0, label: ''}]
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.TYPE_OF_BUSINESS}
          label={OPLabel.TYPE_OF_BUSINESS}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.TYPE_OF_BUSINESS)
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Select
            placeholder={`Choose ${OPLabel.TYPE_OF_BUSINESS}`}
            options={typeOfBusiness}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={24}>
        <Form.Item
          name={OPKey.FINANCIAL_YEAR_TYPE}
          label={OPLabel.FINANCIAL_YEAR_TYPE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.FINANCIAL_YEAR_TYPE)
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Select
            placeholder={`Choose ${OPLabel.FINANCIAL_YEAR_TYPE}`}
            disabled
            options={financialYearDropdown}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.COMPANY_ADDRESS}
          label={OPLabel.COMPANY_ADDRESS}
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: 'Head Office Address'
          }}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.COMPANY_ADDRESS)
            },
            {
              whitespace: true,
              message: CommonErrors.mandatory(OPLabel.COMPANY_ADDRESS)
            }
          ]}
          validateFirst={true}
          hasFeedback
          normalize={(value) => transformInput.removeExtraSpace(value)}
        >
          <Input
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = FirstCap(target.value);
            }}
            placeholder={`Enter ${OPLabel.COMPANY_ADDRESS}`}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.COMPANY_COUNTRY}
          label={OPLabel.COMPANY_COUNTRY}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.COMPANY_COUNTRY)
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Select
            placeholder={`Choose ${OPLabel.COMPANY_COUNTRY}`}
            options={countryOptions}
            showSearch
            disabled
            filterOption={filterSelectOptions}
            onChange={(e) => {
              setCountrySelected(e);
              setPreferredUnits({...preUnits, ...FilteredUnit(e)});
              setFields([
                {
                  name: OPKey.TAX,
                  errors: undefined,
                  value: undefined
                },
                {
                  name: OPKey.IDENTITY,
                  errors: undefined,
                  value: undefined
                },
                {
                  name: OPKey.COMPANY,
                  errors: undefined,
                  value: undefined
                }
              ]);
            }}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.YEAR_OF_INCORP}
          label={OPLabel.YEAR_OF_INCORP}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.YEAR_OF_INCORP)
            }
          ]}
          initialValue={undefined}
          validateFirst={true}
          hasFeedback
        >
          <MomentDatePicker
            disabledDate={(current) => {
              return moment() <= current;
            }}
            inputReadOnly
            picker="year"
            style={{width: '100%'}}
            showNow={false}
            placeholder={`Choose ${OPLabel.YEAR_OF_INCORP}`}
          />
        </Form.Item>
      </Col>
      {/* <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.TOTAL_FLOOR_SPACE}
          label={OPLabel.TOTAL_FLOOR_SPACE}
          style={{width: '100%'}}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.TOTAL_FLOOR_SPACE)
            },
            {
              pattern: RegEx.twoDigitDecimal,
              message: CommonErrors.twoDigitPositive
            }
          ]}
          validateFirst={true}
          hasFeedback
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo?.total_floor_space
          }}
        >
          <NumberInput
            addonAfter={area}
            placeholder={OPLabel.TOTAL_FLOOR_SPACE}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.TOTAL_EMP}
          label={OPLabel.TOTAL_EMP}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.TOTAL_EMP)
            },
            {
              pattern: RegEx.nonDecimal,
              message: CommonErrors.nonDecimalNonNegative
            }
          ]}
          validateFirst={true}
          hasFeedback
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo?.total_no_of_employee
          }}
        >
          <NumberInput placeholder={OPLabel.TOTAL_EMP} />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.FLEET_NUM}
          label={OPLabel.FLEET_NUM}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.FLEET_NUM)
            },
            {
              pattern: RegEx.nonDecimal,
              message: CommonErrors.nonDecimalNonNegative
            }
          ]}
          validateFirst={true}
          hasFeedback
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo?.total_no_of_vehicle
          }}
        >
          <NumberInput placeholder={OPLabel.FLEET_NUM} />
        </Form.Item>
      </Col> */}
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.BUSINESS_EMAIL}
          label={OPLabel.BUSINESS_EMAIL}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.BUSINESS_EMAIL)
            },
            {
              min: 5,
              message: `${OPLabel.BUSINESS_EMAIL} Should be more than 4 Characters`
            },
            {
              max: 320,
              message: `${OPLabel.BUSINESS_EMAIL} Should not be more than 320 Characters`
            },
            {
              pattern: RegEx.email,
              message: CommonErrors.validField(OPLabel.BUSINESS_EMAIL)
            },
            {
              // eslint-disable-next-line @typescript-eslint/promise-function-async
              validator(_, value) {
                if (
                  normalizeEmailForComparison(value) !==
                  normalizeEmailForComparison(userEmailId)
                ) {
                  return Promise.resolve();
                } else {
                  return Promise.reject(
                    new Error(
                      `${OPLabel.PERSONAL_EMAIL} and ${OPLabel.BUSINESS_EMAIL} cannot be the same`
                    )
                  );
                }
              }
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Input placeholder={`Enter ${OPLabel.BUSINESS_EMAIL}`} />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.TELEPHONE}
          label={OPLabel.TELEPHONE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.TELEPHONE)
            },
            {
              // eslint-disable-next-line @typescript-eslint/promise-function-async
              validator(_, value) {
                if (RegEx.telephone.test(value)) {
                  return Promise.resolve();
                } else {
                  return Promise.reject(
                    new Error(CommonErrors.validField(OPLabel.TELEPHONE))
                  );
                }
              }
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <PhoneNumberInput />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.WEBSITE}
          label={OPLabel.WEBSITE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.WEBSITE)
            },
            {
              min: 5,
              message: `${OPLabel.WEBSITE} Should be more than 4 Characters`
            },
            {
              max: 320,
              message: `${OPLabel.WEBSITE} Should not be more than 320 Characters`
            },
            {
              pattern: RegEx.website,
              message: CommonErrors.validField(OPLabel.WEBSITE)
            }
          ]}
          validateFirst={true}
          hasFeedback
        >
          <Input placeholder={`Enter ${OPLabel.WEBSITE}`} />
        </Form.Item>
      </Col>
      {/* <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.TOTAL_REVENUE}
          label={OPLabel.TOTAL_REVENUE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(OPLabel.TOTAL_REVENUE)
            },
            {
              pattern: RegEx.twoDigitDecimal,
              message: CommonErrors.twoDigitPositive
            }
          ]}
          validateFirst={true}
          hasFeedback
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo?.total_revenue
          }}
        >
          <NumberInput
            addonAfter={currency}
            placeholder={OPLabel.TOTAL_REVENUE}
          />
        </Form.Item>
      </Col> */}
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.AUTH_CAP}
          label={OPLabel.AUTH_CAP}
          rules={[
            {
              pattern: RegEx.twoDigitDecimal,
              message: CommonErrors.twoDigitPositive
            }
          ]}
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo.AuthCapital
          }}
          validateFirst={true}
          hasFeedback
        >
          <NumberInput
            placeholder={`Enter ${OPLabel.AUTH_CAP}`}
            addonBefore={preferredUnits.currency}
          />
        </Form.Item>
      </Col>
      <Col className="gutter-row" span={colSpan}>
        <Form.Item
          name={OPKey.PAID_CAP}
          label={OPLabel.PAID_CAP}
          rules={[
            {
              pattern: RegEx.twoDigitDecimal,
              message: CommonErrors.twoDigitPositive
            }
          ]}
          tooltip={{
            icon: <InfoCircleOutlined />,
            title: GDInfo.paidCap
          }}
          validateFirst={true}
          hasFeedback
        >
          <NumberInput
            addonBefore={preferredUnits.currency}
            placeholder={`Enter ${OPLabel.PAID_CAP}`}
          />
        </Form.Item>
      </Col>
      {countrySelected && (
        <CountryWiseCreds country={countrySelected} colSpan={colSpan} />
      )}
    </Row>
  );
};

export default GeneralDisclosureForm;
